import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Lock } from 'lucide-react';

export default function AdminPanel() {
  const { authState } = useAuth();
  const navigate = useNavigate();
  
  const isAuthorized = () => {
    // Still loading auth
    if (authState.loading) return null;
    
    if (!authState.user) return false;
    
    const email = authState.user.email || authState.user.profile?.email;
    const isVerified = authState.user.emailVerified;
    
    return email === 'accounts@heysrilanka.com' && isVerified;
  };
  
  const authStatus = isAuthorized();
  
  // Show loading while auth is being checked
  if (authStatus === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }
  
  // If not authorized, show access denied
  if (!authStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="max-w-md w-full text-center">
          <Lock className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the admin panel. This area is restricted to verified administrators only.
          </p>
          <button
            onClick={() => navigate('/app/profile')}
            className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Back to Profile
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-purple-600" />
          <h1 className="text-4xl font-bold text-gray-900">Admin Panel</h1>
        </div>
        
        {/* Placeholder Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Welcome Card */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-600">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome, Admin</h2>
            <p className="text-gray-600">
              You have full administrative access to the Hey SriLanka platform.
            </p>
          </div>
          
          {/* User Info Card */}
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-600">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Your Account</h2>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-semibold">Email:</span> {authState.user?.email || authState.user?.profile?.email}</p>
              <p><span className="font-semibold">Verified:</span> ✓ Yes</p>
              <p><span className="font-semibold">User ID:</span> {authState.user?.userId}</p>
            </div>
          </div>
        </div>
        
        {/* Placeholder Sections */}
        <div className="space-y-6">
          {/* Dashboard Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Dashboard</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-2">Total Users</h3>
                <p className="text-3xl font-bold text-gray-900">—</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-2">Active Sessions</h3>
                <p className="text-3xl font-bold text-gray-900">—</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-700 mb-2">Total Queries</h3>
                <p className="text-3xl font-bold text-gray-900">—</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">Placeholder - Admin features coming soon</p>
          </div>
          
          {/* Management Section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Management Tools</h2>
            <div className="space-y-3">
              <button className="w-full md:w-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium rounded-lg transition-colors disabled opacity-50 cursor-not-allowed" disabled>
                User Management
              </button>
              <button className="w-full md:w-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium rounded-lg transition-colors disabled opacity-50 cursor-not-allowed" disabled>
                Analytics
              </button>
              <button className="w-full md:w-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium rounded-lg transition-colors disabled opacity-50 cursor-not-allowed" disabled>
                Settings
              </button>
              <p className="text-sm text-gray-500 mt-2">Placeholder - Features coming soon</p>
            </div>
          </div>
          
          {/* Info Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Placeholder Notice</h3>
            <p className="text-blue-800 text-sm">
              This is a placeholder admin panel. Admin features and management tools will be added here as needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

